import { useCallback, useEffect, useState } from 'react'
import { ChevronDown, ChevronRight, Brain, AlertTriangle, Check, Copy } from 'lucide-react'
import { apiFetch } from '../lib/apiFetch'

// Lightweight per-call row served in the task-detail envelope (see lib/llm-jobs.ts listLlmJobMetaByTaskIds).
// The big request/result bodies are NOT here — they load on demand when a row is expanded.
export interface LlmJobMeta {
  id: string
  label: string | null
  model: string | null
  status: string
  error: string | null
  created_at: string
  processing_started_at: string | null
  completed_at: string | null
  msg_count: number | null
  tool_count: number | null
  max_tokens: number | null
  request_bytes: number | null
  result_bytes: number | null
  usage: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number } | null
  finish_reason: string | null
  provider: string | null
  tool_names: string[] | null
  // Backend LLM errors recorded against this call even when it ended 'completed' (recovered fallback,
  // slow watchdog, truncation-retry). Distinct error_source values; severity is the worst of them.
  backend_error_sources: string[] | null
  backend_error_severity: string | null
}

// One tool exactly as the model was offered it. `parameters` is JSON Schema; only the parts this UI
// renders are typed. A closed `enum` IS the model's option set for that field, so it is spelled out
// rather than summarised — a decision that looks wrong is only diagnosable with the choices the model
// actually had, and the wording that told it what each one means, in view.
interface LlmToolDef {
  function?: {
    name?: string
    description?: string
    parameters?: {
      required?: string[]
      properties?: Record<string, { type?: string; enum?: unknown[]; description?: string }>
    }
  }
}

// The full row loaded from GET /api/llm-job/:id when a call is expanded.
interface LlmJobFull {
  id: string
  model: string | null
  label: string | null
  status: string
  error: string | null
  request: {
    messages?: Array<{ role: string; content: unknown; tool_calls?: Array<{ function?: { name?: string; arguments?: string } }> }>
    tools?: LlmToolDef[]
    maxTokens?: number
    reasoningEffort?: string
    temperature?: number
  }
  result: Record<string, unknown> | null
  cost_usd: number | null
}

interface UsageTotals {
  prompt: number
  completion: number
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}
function formatElapsed(start: string | null, end: string | null): string {
  if (!start || !end) return ''
  const ms = new Date(end).getTime() - new Date(start).getTime()
  if (!Number.isFinite(ms) || ms < 0) return ''
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`
}
function formatBytes(n: number | null): string {
  if (n == null) return ''
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)}MB`
  if (n >= 1024) return `${Math.round(n / 1024)}KB`
  return `${n}B`
}
function contentToString(content: unknown): string {
  if (typeof content === 'string') return content
  if (content == null) return ''
  return JSON.stringify(content, null, 2)
}

function usageNumber(value: unknown): number {
  const number = typeof value === 'string' ? Number(value) : typeof value === 'number' ? value : 0
  return Number.isFinite(number) ? number : 0
}

// A retried/fallback call can have several billable generations. In that case the provider charged for
// every entry in billable_attempts; result.usage is already the final entry and must not be added again.
function billableUsage(result: Record<string, unknown> | null): UsageTotals | null {
  if (!result) return null
  const attempts = Array.isArray(result.billable_attempts) ? result.billable_attempts : []
  const usages = attempts.length > 0
    ? attempts.flatMap(attempt => {
      if (!attempt || typeof attempt !== 'object') return []
      const usage = (attempt as Record<string, unknown>).usage
      return usage && typeof usage === 'object' && !Array.isArray(usage) ? [usage as Record<string, unknown>] : []
    })
    : result.usage && typeof result.usage === 'object' && !Array.isArray(result.usage)
      ? [result.usage as Record<string, unknown>]
      : []
  if (usages.length === 0) return null
  return usages.reduce<UsageTotals>((total, usage) => ({
    prompt: total.prompt + usageNumber(usage.prompt_tokens),
    completion: total.completion + usageNumber(usage.completion_tokens),
  }), { prompt: 0, completion: 0 })
}

function formatUsd(cost: number | null): string {
  if (cost == null || !Number.isFinite(cost)) return 'Unpriced'
  return cost >= 1 ? `$${cost.toFixed(2)}` : `$${cost.toFixed(6)}`
}

function messageText(message: { content: unknown; tool_calls?: Array<{ function?: { name?: string; arguments?: string } }> }): string {
  return contentToString(message.content) + (message.tool_calls?.map(tc => `\n[${tc.function?.name}] ${tc.function?.arguments ?? ''}`).join('') ?? '')
}

// Array.from counts Unicode code points rather than JavaScript's UTF-16 code units, so an emoji or
// other astral character reads as one character in the UI instead of two.
function messageCharacterCount(message: { content: unknown; tool_calls?: Array<{ function?: { name?: string; arguments?: string } }> }): number {
  return Array.from(messageText(message)).length
}

function characterCount(value: unknown): number {
  const text = typeof value === 'string' ? value : JSON.stringify(value) ?? ''
  return Array.from(text).length
}

function outputCharacterCount(result: Record<string, unknown> | null): number {
  if (!result) return 0
  return characterCount(result.output) + (result.tool_calls == null ? 0 : characterCount(result.tool_calls))
}

function MessageCopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  async function copy() {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard access may be unavailable in an insecure or background tab.
    }
  }
  return (
    <button type="button" onClick={copy} aria-label={copied ? 'Copied' : 'Copy message'} title={copied ? 'Copied' : 'Copy message'} data-testid="llm-message-copy" style={{ display: 'inline-flex', alignItems: 'center', gap: 3, border: 'none', background: 'none', color: 'inherit', cursor: 'pointer', padding: 2, opacity: 0.75 }}>
      {copied ? <Check size={12} aria-hidden /> : <Copy size={12} aria-hidden />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

// The failure/degradation badges for a call. A hard failure (status 'failed') is always a red badge.
// Otherwise a 'completed' call may still have recorded backend errors (recovered fallback, slow
// watchdog, truncation-retry) — surface each error_source, coloured by its severity, so a benign
// fallback reads as a warning and a hard error as red. finish_reason=length is a backstop for a final
// truncation that (rarely) left no backend record.
function callBadges(job: LlmJobMeta): Array<{ label: string; severity: 'error' | 'warning' }> {
  const out: Array<{ label: string; severity: 'error' | 'warning' }> = []
  const add = (label: string, severity: 'error' | 'warning') => {
    if (!out.some(b => b.label === label)) out.push({ label, severity })
  }
  if (job.status === 'failed') add('failed', 'error')
  const severity = job.backend_error_severity === 'warning' ? 'warning' : 'error'
  for (const src of job.backend_error_sources ?? []) {
    add(src.replace(/^llm-/, '').replace(/^length$/, 'truncated'), severity)
  }
  if (job.finish_reason === 'length') add('truncated', 'error')
  return out
}

// One expandable call row: the metadata line, and — once expanded — the full request/result fetched
// lazily from /api/llm-job/:id.
function CallRow({ job, classPrefix, deepLinked }: { job: LlmJobMeta; classPrefix: string; deepLinked: boolean }) {
  const [expanded, setExpanded] = useState(false)
  const [full, setFull] = useState<LlmJobFull | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)

  const load = useCallback(async () => {
    if (full || loading) return
    setLoading(true); setLoadError(false)
    try {
      const res = await apiFetch(`/api/llm-job/${job.id}`)
      if (!res.ok) { setLoadError(true); return }
      const data = await res.json() as { job: LlmJobFull }
      setFull(data.job)
    } catch {
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [full, loading, job.id])

  // Arrived here from an action's "view prompt" link (#llm-call-<id>): open this call and fetch its bodies,
  // so the prompt behind a recorded decision is one click away rather than a row the reader must hunt for.
  useEffect(() => {
    if (!deepLinked) return
    setExpanded(true)
    void load()
  }, [deepLinked, load])

  async function toggle() {
    const next = !expanded
    setExpanded(next)
    if (next) await load()
  }

  const prompt = job.usage?.prompt_tokens
  const completion = job.usage?.completion_tokens
  const badges = callBadges(job)

  return (
    <div id={`llm-call-${job.id}`} className={`${classPrefix}__llm-call`} style={{ borderTop: '1px solid var(--border-color, #2a2a2a)', ...(deepLinked ? { outline: '1px solid var(--accent-color, #4a9eff)', outlineOffset: 2 } : {}) }}>
      <button
        type="button"
        onClick={toggle}
        className={`${classPrefix}__llm-call-head`}
        data-testid="llm-call-row"
        data-analytics-event="llm-call-toggle"
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: '6px 4px', fontSize: 13, flexWrap: 'wrap' }}
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span style={{ opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}>{formatTime(job.created_at)}</span>
        <span style={{ fontWeight: 600 }}>{job.model ?? 'model?'}</span>
        {badges.map(b => (
          <span key={b.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: b.severity === 'warning' ? '#d9930a' : '#e5534b' }}>
            <AlertTriangle size={11} /> {b.label}
          </span>
        ))}
        {(prompt != null || completion != null) && (
          <span style={{ opacity: 0.8, fontVariantNumeric: 'tabular-nums' }}>
            {prompt ?? '?'}→{completion ?? '?'} tok
          </span>
        )}
        {job.max_tokens != null && <span style={{ opacity: 0.5 }}>/{job.max_tokens} max</span>}
        {job.finish_reason && <span style={{ opacity: 0.6 }}>finish={job.finish_reason}</span>}
        {formatElapsed(job.processing_started_at ?? job.created_at, job.completed_at) && (
          <span style={{ opacity: 0.6 }}>{formatElapsed(job.processing_started_at ?? job.created_at, job.completed_at)}</span>
        )}
        {job.tool_names && job.tool_names.length > 0 && (
          <span style={{ opacity: 0.75 }}>→ {job.tool_names.filter(Boolean).join(', ')}</span>
        )}
        <span style={{ marginLeft: 'auto', opacity: 0.45, fontSize: 11 }}>
          req {formatBytes(job.request_bytes)}{job.result_bytes ? ` · res ${formatBytes(job.result_bytes)}` : ''}
        </span>
      </button>

      {expanded && (
        <div style={{ padding: '4px 4px 12px 26px', fontSize: 12 }}>
          {loading && <p style={{ opacity: 0.6 }}>Loading…</p>}
          {loadError && <p style={{ color: '#e5534b' }}>Failed to load call detail.</p>}
          {job.error && <Block label="Error"><span style={{ color: '#e5534b' }}>{job.error}</span></Block>}
          {full && (
            <>
              {(() => {
                const usage = billableUsage(full.result)
                return (
                  <Block label="Usage & cost">
                    <div data-testid="llm-call-usage" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>
                      <span><strong>{usage ? usage.prompt.toLocaleString() : '—'}</strong> input tokens · <strong>{characterCount(full.request).toLocaleString()}</strong> chars</span>
                      <span><strong>{usage ? usage.completion.toLocaleString() : '—'}</strong> output tokens · <strong>{outputCharacterCount(full.result).toLocaleString()}</strong> chars</span>
                      <span><strong>{formatUsd(full.cost_usd)}</strong> total cost</span>
                    </div>
                  </Block>
                )
              })()}
              {full.request?.tools && full.request.tools.length > 0 && (
                <Block label={`Tools offered (${full.request.tools.length})`}>
                  {full.request.tools.map((t, i) => <ToolDef key={t.function?.name ?? i} fn={t.function} />)}
                </Block>
              )}
              <Block label={`Messages in (${full.request?.messages?.length ?? 0})`}>
                {(full.request?.messages ?? []).map((m, i) => (
                  <div key={i} style={{ marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: 0.7, fontWeight: 600 }}>
                      <span>{m.role}</span>
                      {m.tool_calls && m.tool_calls.length > 0 && <span>· tool_calls: {m.tool_calls.map(tc => tc.function?.name).filter(Boolean).join(', ')}</span>}
                      <span style={{ fontWeight: 400 }}>· {messageCharacterCount(m).toLocaleString()} chars</span>
                      <span style={{ marginLeft: 'auto' }}><MessageCopyButton text={messageText(m)} /></span>
                    </div>
                    <Pre>{contentToString(m.content)}
                      {m.tool_calls && m.tool_calls.length > 0
                        ? m.tool_calls.map(tc => `\n[${tc.function?.name}] ${tc.function?.arguments ?? ''}`).join('')
                        : ''}
                    </Pre>
                  </div>
                ))}
              </Block>
              <Block label="Result">
                <Pre>{full.result ? JSON.stringify(full.result, null, 2) : '(no result)'}</Pre>
              </Block>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// One offered tool: its name, the description that told the model WHEN to pick it, and one line per
// parameter — type or enum values, whether it was required, and its description. This section used to
// print `function.name` alone, which meant the option set behind a recorded decision was fetched from
// /api/llm-job/:id and then dropped: a verdict whose meaning lives entirely in an enum value's
// description (e.g. an `off_track` that only ever meant "an auth wall") could not be read back here.
function ToolDef({ fn }: { fn: LlmToolDef['function'] }) {
  const properties = fn?.parameters?.properties ?? {}
  const required = new Set(fn?.parameters?.required ?? [])
  return (
    <div style={{ marginBottom: 8 }} data-testid="llm-call-tool">
      <div style={{ fontWeight: 600 }}>{fn?.name ?? '(unnamed)'}</div>
      {fn?.description && <Pre>{fn.description}</Pre>}
      {Object.entries(properties).map(([name, p]) => (
        <div key={name} style={{ marginTop: 4, paddingLeft: 8 }}>
          <span style={{ fontWeight: 600 }}>{name}</span>
          <span style={{ opacity: 0.6 }}>
            {' · '}{p.enum ? p.enum.map(String).join(' | ') : p.type ?? '?'}{required.has(name) ? ' · required' : ''}
          </span>
          {p.description && <div style={{ opacity: 0.75 }}>{p.description}</div>}
        </div>
      ))}
    </div>
  )
}

function Block({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ opacity: 0.55, textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.5, marginBottom: 2 }}>{label}</div>
      {children}
    </div>
  )
}
function Pre({ children }: { children: React.ReactNode }) {
  return (
    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 320, overflow: 'auto', margin: 0, padding: 8, background: 'rgba(127,127,127,0.08)', borderRadius: 4, fontSize: 11.5 }}>
      {children}
    </pre>
  )
}

function labelText(label: string | null): string {
  return label && label.trim() ? label : '(unlabeled)'
}

/**
 * Collapsible list of EVERY LLM call a task made. Calls that share a label AND run back-to-back are
 * grouped into one run (e.g. a feedback-select loop, then a feedback-execute loop); a label that
 * recurs after a different label starts a fresh group. Each call row expands to show the messages and
 * tools passed in, the completion (output + tool_calls + usage), and any error — the full bodies load
 * on demand from /api/llm-job/:id. Admin-only (rendered only inside the admin task page).
 */
export default function LlmCallsSection({ jobs, classPrefix }: { jobs: LlmJobMeta[]; classPrefix: string }) {
  const [expanded, setExpanded] = useState(false)
  // The call an action linked to (#llm-call-<id>). Tracked as state, and re-read on hashchange, because the
  // link is same-page: without the listener a second click (or a click while the section is already open)
  // would change the URL and do nothing visible.
  const [linkedId, setLinkedId] = useState<string | null>(null)
  useEffect(() => {
    const read = () => {
      const hash = window.location.hash
      const id = hash.startsWith('#llm-call-') ? hash.slice('#llm-call-'.length) : null
      if (!id || !jobs.some(j => j.id === id)) return
      setLinkedId(id)
      setExpanded(true)
      // The row only exists once the section is open, so scroll on the next frame.
      window.setTimeout(() => document.getElementById(`llm-call-${id}`)?.scrollIntoView({ block: 'center' }), 50)
    }
    read()
    window.addEventListener('hashchange', read)
    return () => window.removeEventListener('hashchange', read)
  }, [jobs])

  if (!jobs.length) return null

  // Group CONSECUTIVE same-label calls (jobs arrive oldest-first). A new group starts whenever the
  // label changes from the previous call, so a label that recurs later forms its own run.
  const groups: Array<{ label: string; jobs: LlmJobMeta[] }> = []
  for (const j of jobs) {
    const key = labelText(j.label)
    const last = groups[groups.length - 1]
    if (last && last.label === key) last.jobs.push(j)
    else groups.push({ label: key, jobs: [j] })
  }

  const totalTokens = jobs.reduce((sum, j) => sum + (j.usage?.total_tokens ?? 0), 0)
  // Calls that failed or degraded (any badge) — surfaced in the collapsed header so failures are
  // visible without expanding the whole list.
  const failedCount = jobs.filter(j => callBadges(j).length > 0).length

  return (
    <section className={`${classPrefix}__section`}>
      <button type="button" className={`${classPrefix}__section-toggle`} onClick={() => setExpanded(!expanded)} data-analytics-event="llm-calls-toggle" data-testid="llm-calls-toggle">
        {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        <Brain size={15} /> LLM Calls ({jobs.length}{totalTokens ? `, ${totalTokens.toLocaleString()} tok` : ''})
        {failedCount > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#e5534b', marginLeft: 6 }} data-testid="llm-calls-failed-count">
            <AlertTriangle size={12} /> {failedCount} failed
          </span>
        )}
      </button>
      {expanded && (
        <div className={`${classPrefix}__logs`}>
          {groups.map((g, gi) => {
            const groupTokens = g.jobs.reduce((s, j) => s + (j.usage?.total_tokens ?? 0), 0)
            return (
              <div key={`${g.label}-${gi}`} style={{ marginBottom: 10 }} data-testid="llm-call-group">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', fontSize: 12, fontWeight: 600, opacity: 0.85, borderBottom: '1px solid var(--border-color, #2a2a2a)' }}>
                  <span>{g.label}</span>
                  <span style={{ opacity: 0.6, fontWeight: 400 }}>
                    {g.jobs.length} {g.jobs.length === 1 ? 'call' : 'calls'}{groupTokens ? ` · ${groupTokens.toLocaleString()} tok` : ''}
                  </span>
                </div>
                {g.jobs.map(j => <CallRow key={j.id} job={j} classPrefix={classPrefix} deepLinked={j.id === linkedId} />)}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}
